import React, { useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    Typography,
    Container,
    IconButton,
    Box,
    InputAdornment,
    Alert,
    Fade,
    CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../../redux/actions/authAction';
import { fetchSubscription } from '../../redux/actions/subscriptionAction';
import { fetchPricing } from '../../redux/actions/pricingActions';
import { backendClient } from '../../api/backend';
import CustomButton from '../../components/CustomButton';
import LanguageSelector from '../../components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { isMobile } from '../../utils/utils';

const GOLD = '#c9a96e';

const fadeInUpKeyframes = {
    '@keyframes fadeInUp': {
        from: { opacity: 0, transform: 'translateY(24px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
};

const stagger = (index) => ({
    opacity: 0,
    animation: 'fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards',
    animationDelay: `${index * 0.08}s`,
});

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
        color: '#fff',
        transition: 'all 0.2s ease',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.08)', transition: 'border-color 0.2s ease' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.16)' },
        '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: '1.5px' },
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px #111111 inset',
            WebkitTextFillColor: '#fff',
            caretColor: '#fff',
            borderRadius: '12px',
            transition: 'background-color 5000s ease-in-out 0s',
        },
    },
    '& .MuiInputLabel-root': {
        fontFamily: '"DM Sans", sans-serif', color: 'rgba(255, 255, 255, 0.35)', fontSize: '14px',
        '&.Mui-focused': { color: GOLD },
    },
    '& .MuiFormHelperText-root': { fontFamily: '"DM Sans", sans-serif' },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.25)', fontSize: '20px' },
};

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const ExternalInvitationScreen = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const query = useQuery();
    const token = query.get('token');
    const isMobileView = isMobile();

    const [tokenInfo, setTokenInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    const [registering, setRegistering] = useState(false);

    useEffect(() => {
        const verifyToken = async () => {
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(
                    atob(base64)
                        .split('')
                        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                        .join('')
                );
                const payload = JSON.parse(jsonPayload);

                if (!payload.external_collab || !payload.email) {
                    throw new Error('Invalid external collaboration token');
                }

                setTokenInfo(payload);
                setEmail(payload.email);

                // Set page language from invitation token
                const lang = payload.language;
                if (lang && ['fr', 'en', 'ar', 'de', 'it'].includes(lang) && i18n.language !== lang) {
                    i18n.changeLanguage(lang);
                }
            } catch (err) {
                console.error('Error verifying external invitation token:', err);
                setError(t('external_invitation.invalid_token', 'Invalid or expired invitation link'));
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            verifyToken();
        } else {
            setError(t('external_invitation.missing_token', 'Invitation token is missing'));
            setLoading(false);
        }
    }, [token, t]);

    const handleSubmit = async () => {
        const isValidPassword = password.length >= 6 && /[!@#$%^&*(),.+?":{}|<>]/.test(password);
        const isPasswordMatch = password === confirmPassword;

        setPasswordError(!isValidPassword);
        setConfirmPasswordError(!isPasswordMatch);

        if (!isValidPassword || !isPasswordMatch) return;

        setRegistering(true);
        setError('');

        try {
            const response = await backendClient.post('api/user/register-external', {
                email,
                password,
                token,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Error registering account');
            }

            const data = await response.json();

            if (data.token && data.user) {
                localStorage.setItem('access_token', data.token.access_token);
                localStorage.setItem('token_type', data.token.token_type || 'bearer');
                if (data.token.token_version) {
                    localStorage.setItem('token_version', data.token.token_version);
                }

                // Store contract_id for post-onboarding redirect
                if (data.contract_id) {
                    localStorage.setItem('redirect_after_onboarding', data.contract_id);
                    console.log('[ExternalInvitation] stored redirect_after_onboarding=', data.contract_id);
                } else {
                    console.log('[ExternalInvitation] WARNING: no contract_id in response');
                }

                dispatch(loginSuccess(data));
                dispatch(fetchSubscription());
                dispatch(fetchPricing());

                // Navigate based on OTP requirement
                console.log('[ExternalInvitation] response:', { requires_otp: data.requires_otp, is_boarded: data.user.is_boarded, contract_id: data.contract_id });
                if (data.requires_otp) {
                    console.log('[ExternalInvitation] → /verify-otp');
                    navigate('/verify-otp', { state: { email: data.email || email } });
                } else if (!data.user.is_boarded) {
                    console.log('[ExternalInvitation] → /onboarding');
                    navigate('/onboarding');
                } else {
                    // Already boarded (existing user) — go to contract
                    if (data.contract_id) {
                        navigate(`/contract/${data.contract_id}`);
                    } else {
                        navigate('/');
                    }
                }
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (err) {
            console.error('Error registering external user:', err);
            if (err.message?.includes('already exists')) {
                setError(t('email_already_exists', 'An account with this email already exists.'));
            } else if (err.message?.includes('expired')) {
                setError(t('external_invitation.expired_token', 'Your invitation link has expired. Please ask the sender for a new link.'));
            } else {
                setError(err.message || t('registration_error', 'Error registering account'));
            }
        } finally {
            setRegistering(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleSubmit();
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#000' }}>
                <CircularProgress sx={{ color: GOLD }} />
            </Box>
        );
    }

    // Fatal error (bad token)
    if (error && !tokenInfo) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#000', px: 3 }}>
                <Box sx={{ textAlign: 'center', maxWidth: 420 }}>
                    <Alert severity="error" sx={{
                        mb: 3, borderRadius: '12px', fontFamily: '"DM Sans", sans-serif',
                        backgroundColor: 'rgba(239, 68, 68, 0.08)', color: '#fca5a5',
                        border: '1px solid rgba(239, 68, 68, 0.15)',
                        '& .MuiAlert-icon': { color: '#f87171' },
                    }}>
                        {error}
                    </Alert>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.4)' }}>
                        {t('external_invitation.contact_sender', 'Please contact the person who sent you this invitation.')}
                    </Typography>
                </Box>
            </Box>
        );
    }

    const inviterOrgName = tokenInfo?.inviter_org_name || '';

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#000', display: 'flex', position: 'relative', overflow: 'hidden', ...fadeInUpKeyframes }}>
            {/* Grain texture overlay */}
            <Box sx={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.025, pointerEvents: 'none', zIndex: 1,
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'repeat', backgroundSize: '256px 256px',
            }} />

            {/* Language selector */}
            <Box sx={{ position: 'absolute', top: 20, right: 24, zIndex: 10 }}>
                <LanguageSelector variant="dark" />
            </Box>

            <Grid container sx={{ minHeight: '100vh', position: 'relative', zIndex: 2 }}>
                {/* Left Column — Branding */}
                {!isMobileView && (
                    <Grid item xs={12} md={5} sx={{
                        background: '#000', display: 'flex', flexDirection: 'column',
                        justifyContent: 'center', alignItems: 'center', position: 'relative', padding: 6,
                    }}>
                        <Box sx={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(201, 169, 110, 0.05) 0%, transparent 70%)',
                            pointerEvents: 'none',
                        }} />
                        <Box sx={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.025, pointerEvents: 'none',
                            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                            backgroundSize: '80px 80px',
                        }} />

                        {/* Logo */}
                        <Box sx={{ ...stagger(0), mb: 5 }}>
                            <Box sx={{
                                width: 100, height: 100, borderRadius: '50%', display: 'flex',
                                justifyContent: 'center', alignItems: 'center',
                                border: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'rgba(255,255,255,0.02)', overflow: 'hidden',
                            }}>
                                <img src="/logo/logo-circle-dark-mode.svg" alt="Lawis Logo"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }} />
                            </Box>
                        </Box>

                        <Typography sx={{
                            ...stagger(1), fontFamily: '"Cormorant Garamond", Georgia, serif',
                            fontWeight: 300, fontSize: 'clamp(2rem, 3.5vw, 3.2rem)', color: '#fff',
                            textAlign: 'center', letterSpacing: '-0.02em', lineHeight: 1.1, mb: 2.5, zIndex: 2, position: 'relative',
                        }}>
                            {t('external_invitation.title', 'Collaborate on Lawis')}
                        </Typography>

                        <Typography sx={{
                            ...stagger(2), fontFamily: '"DM Sans", sans-serif', fontWeight: 400,
                            fontSize: '15px', color: 'rgba(255, 255, 255, 0.40)', textAlign: 'center',
                            maxWidth: '380px', lineHeight: 1.7, zIndex: 2, position: 'relative',
                        }}>
                            {t('external_invitation.subtitle', 'Create your account to review the contract shared with you.')}
                        </Typography>

                        <Box sx={{ ...stagger(3), width: '48px', height: '1.5px', backgroundColor: GOLD, opacity: 0.4, mt: 4, borderRadius: '1px' }} />
                    </Grid>
                )}

                {/* Right Column — Form */}
                <Grid item xs={12} md={7} sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column',
                    backgroundColor: '#0d0d0d',
                    borderLeft: isMobileView ? 'none' : '1px solid rgba(255, 255, 255, 0.04)',
                    position: 'relative', overflowY: 'auto', maxHeight: '100vh', py: 4,
                }}>
                    {isMobileView && (
                        <Box sx={{ ...stagger(0), mb: 3, mt: 2 }}>
                            <Box sx={{
                                width: 72, height: 72, borderRadius: '50%', display: 'flex',
                                justifyContent: 'center', alignItems: 'center',
                                border: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'rgba(255,255,255,0.02)', overflow: 'hidden',
                            }}>
                                <img src="/logo/logo-circle-dark-mode.svg" alt="Lawis Logo"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '8px' }} />
                            </Box>
                        </Box>
                    )}

                    <Container maxWidth="sm" sx={{ maxWidth: '520px !important', px: isMobileView ? 3 : 4 }}>
                        <Typography sx={{
                            ...stagger(0), fontFamily: '"DM Sans", sans-serif', fontSize: '11px',
                            fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase',
                            color: 'rgba(255, 255, 255, 0.25)', mb: 2,
                        }}>
                            {t('external_invitation.create_account', 'CREATE YOUR ACCOUNT')}
                        </Typography>

                        <Typography sx={{
                            ...stagger(1), fontFamily: '"Cormorant Garamond", Georgia, serif',
                            fontWeight: 300, fontSize: isMobileView ? '1.8rem' : '2.4rem',
                            color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.1, mb: 1,
                        }}>
                            {t('external_invitation.heading', 'Set up your account')}
                        </Typography>

                        <Box sx={{ ...stagger(2), width: '32px', height: '1.5px', backgroundColor: GOLD, opacity: 0.5, mb: 3.5, mt: 2, borderRadius: '1px' }} />

                        {/* Inviter badge */}
                        {inviterOrgName && (
                            <Box sx={{
                                ...stagger(2), display: 'flex', alignItems: 'center', gap: 1.5,
                                mb: 3, px: 2, py: 1.5, borderRadius: '10px',
                                backgroundColor: 'rgba(201,169,110,0.07)', border: '1px solid rgba(201,169,110,0.2)',
                            }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: GOLD, flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.55)' }}>
                                    {t('external_invitation.invited_by', 'Invited by')}{' '}
                                    <Typography component="span" sx={{ color: GOLD, fontWeight: 700, fontFamily: 'inherit', fontSize: 'inherit' }}>
                                        {inviterOrgName}
                                    </Typography>
                                </Typography>
                            </Box>
                        )}

                        {/* Error alert */}
                        {error && (
                            <Fade in={!!error}>
                                <Alert severity="error" sx={{
                                    mb: 3, borderRadius: '12px', fontFamily: '"DM Sans", sans-serif',
                                    backgroundColor: 'rgba(239, 68, 68, 0.08)', color: '#fca5a5',
                                    border: '1px solid rgba(239, 68, 68, 0.15)',
                                    '& .MuiAlert-icon': { color: '#f87171' },
                                }}>
                                    {error}
                                </Alert>
                            </Fade>
                        )}

                        {/* Form */}
                        <Box sx={stagger(3)}>
                            <Grid container spacing={2}>
                                {/* Email — disabled, pre-filled */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth label={t('auth_email_label', 'Email')}
                                        value={email} disabled
                                        InputProps={{
                                            startAdornment: (<InputAdornment position="start"><Email /></InputAdornment>),
                                        }}
                                        sx={{
                                            ...inputSx,
                                            '& .MuiOutlinedInput-root.Mui-disabled': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.05)' },
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': { color: 'rgba(255, 255, 255, 0.25)' },
                                            '& .MuiOutlinedInput-input.Mui-disabled': { WebkitTextFillColor: 'rgba(255, 255, 255, 0.5)' },
                                        }}
                                    />
                                </Grid>

                                {/* Password */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth label={t('auth_password_label', 'Password')}
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={e => { setPassword(e.target.value); setPasswordError(false); }}
                                        error={passwordError}
                                        helperText={passwordError ? t('auth_password_requirements', 'Min. 6 characters with a special character') : ''}
                                        required onKeyPress={handleKeyPress}
                                        InputProps={{
                                            startAdornment: (<InputAdornment position="start"><Lock /></InputAdornment>),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'rgba(255,255,255,0.25)' }}>
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={inputSx}
                                    />
                                </Grid>

                                {/* Confirm Password */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth label={t('auth_confirm_password_label', 'Confirm Password')}
                                        type={showPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={e => { setConfirmPassword(e.target.value); setConfirmPasswordError(false); }}
                                        error={confirmPasswordError}
                                        helperText={confirmPasswordError ? t('auth_passwords_dont_match', 'Passwords do not match') : ''}
                                        required onKeyPress={handleKeyPress}
                                        InputProps={{
                                            startAdornment: (<InputAdornment position="start"><Lock /></InputAdornment>),
                                        }}
                                        sx={inputSx}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Submit button */}
                        <Box sx={{ ...stagger(4), mt: 3 }}>
                            <CustomButton
                                text={registering ? t('auth_creating_account', 'Creating account...') : t('external_invitation.create_account_btn', 'Create account')}
                                disabled={registering || !password || !confirmPassword}
                                loading={registering}
                                outlined={false}
                                onClick={handleSubmit}
                                sx={{
                                    width: '100%', py: 1.8, borderRadius: '12px',
                                    fontFamily: '"DM Sans", sans-serif', fontSize: '15px', fontWeight: 600,
                                    backgroundColor: GOLD, color: '#1c1917',
                                    '&:hover': { backgroundColor: '#b8963f' },
                                }}
                            />
                        </Box>

                        {/* Already have account link */}
                        <Box sx={{ ...stagger(5), textAlign: 'center', mt: 3 }}>
                            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.35)' }}>
                                {t('auth_already_have_account', 'Already have an account?')}{' '}
                                <Typography component="span" onClick={() => navigate('/login')}
                                    sx={{ color: GOLD, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', '&:hover': { textDecoration: 'underline' } }}>
                                    {t('auth_login_link', 'Sign in')}
                                </Typography>
                            </Typography>
                        </Box>
                    </Container>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ExternalInvitationScreen;
